import React, { useState, useEffect } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  CircularProgress
} from '@mui/material';
import client from '../api/client';
import AnimatedSection from '../components/AnimatedSection';
import { motion } from 'framer-motion';
import CountdownTimer from '../components/CountdownTimer';

const countries = [
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina", "Armenia", "Australia", "Austria", "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi", "Cabo Verde", "Cambodia", "Cameroon", "Canada", "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo", "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czech Republic", "Denmark", "Djibouti", "Dominica", "Dominican Republic", "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini", "Ethiopia", "Fiji", "Finland", "France", "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana", "Haiti", "Honduras", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Jamaica", "Japan", "Jordan", "Kazakhstan", "Kenya", "Kiribati", "Kuwait", "Kyrgyzstan", "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg", "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar", "Namibia", "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Korea", "North Macedonia", "Norway", "Oman", "Pakistan", "Palau", "Palestine", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal", "Qatar", "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa", "South Korea", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria", "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu", "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States", "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City", "Venezuela", "Vietnam", "Yemen", "Zambia", "Zimbabwe"
];

const categories = [
  'Media Team',
  'Logistics',
  'Ushers - Main day and hackathon',
  'Administrative'
];

const mediaRoles = [
  'Social Media Enthusiasts',
  'Content Writers',
  'Live Streaming',
];

function VolunteerRegister() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    nationality: '',
    skills: '',
    availability: [], // Now an array
    interest: '',
    address: '',
    category: '',
    mediaRole: '',
    gender: '',
    tshirtSize: '',
    website: ''
  });
  const availabilityOptions = [
    'Dec 11, 2025 (Hackathon)',
    'Dec 12, 2025 (Hackathon)',
    'Dec 13, 2025 (Markathon)'
  ];

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [deadlinePassed, setDeadlinePassed] = useState(false);
  const [volunteerDeadline, setVolunteerDeadline] = useState(null);

  // Fallback deadline if backend fails
  const fallbackDeadline = new Date('2025-11-27T23:59:59');

  useEffect(() => {
    let intervalId;
    const fetchDeadline = async () => {
      try {
        const res = await client.get('/api/admin-deadline');
        if (res.data && res.data.volunteerDeadline) {
          // Always parse as Date object in UTC
          let deadlineRaw = res.data.volunteerDeadline;
          // If the string is in 'YYYY-MM-DD HH:mm:ss' format, convert to ISO
          if (/^\d{4}-\d{2}-\d{2} /.test(deadlineRaw)) {
            deadlineRaw = deadlineRaw.replace(' ', 'T') + 'Z';
          }
          const deadlineDate = new Date(deadlineRaw);
          setVolunteerDeadline(deadlineDate);
          // Debug logging
          console.log('[Volunteer] Raw deadline from API:', deadlineRaw, '| typeof:', typeof deadlineRaw);
          console.log('[Volunteer] Parsed deadline as Date:', deadlineDate, '| isNaN:', isNaN(deadlineDate));
          console.log('[Volunteer] Now:', new Date(), '| Now > deadline:', new Date() > deadlineDate);
          if (new Date() > deadlineDate) {
            setDeadlinePassed(true);
          } else {
            setDeadlinePassed(false);
          }
        } else {
          setVolunteerDeadline(null);
          setDeadlinePassed(false);
        }
      } catch (err) {
        setVolunteerDeadline(null);
        setDeadlinePassed(false);
        console.error('[Volunteer] Error fetching deadline:', err);
      }
    };
    fetchDeadline();
    intervalId = setInterval(fetchDeadline, 30000);
    return () => clearInterval(intervalId);
  }, []);

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    if (name === 'availability') {
      setFormData((prev) => {
        let newArr = prev.availability || [];
        if (checked) {
          newArr = [...newArr, value];
        } else {
          newArr = newArr.filter((v) => v !== value);
        }
        return { ...prev, availability: newArr };
      });
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    // Validate required fields
    const requiredFields = ['fullName', 'email', 'phone', 'nationality', 'skills', 'availability', 'interest', 'category', 'gender', 'tshirtSize'];
    const missingFields = requiredFields.filter(field => {
      if (field === 'availability') {
        return !formData.availability || formData.availability.length === 0;
      }
      return !formData[field];
    });

    if (missingFields.length > 0) {
      setMessage({ type: 'error', text: `Please fill in all required fields: ${missingFields.join(', ')}` });
      setLoading(false);
      return;
    }

    // Additional validation for media team category
    if (formData.category === 'Media Team' && !formData.mediaRole) {
      setMessage({ type: 'error', text: 'Please select your media role for the Media Team category.' });
      setLoading(false);
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setMessage({ type: 'error', text: 'Please enter a valid email address.' });
      setLoading(false);
      return;
    }

    try {
      const response = await client.post('/.netlify/functions/volunteer-register', {
        ...formData,
        availability: formData.availability.join(', ')
      });

      // Check if the response indicates success
      if (response.data && response.data.success === false) {
        // Display backend error messages
        if (response.data.errors && Array.isArray(response.data.errors)) {
          setMessage({
            type: 'error',
            text: response.data.errors.join(', ')
          });
        } else {
          setMessage({
            type: 'error',
            text: response.data.error || 'Registration failed. Please try again.'
          });
        }
        setLoading(false);
        return;
      }

      setMessage({ type: 'success', text: 'Volunteer registration submitted successfully! Check your email for a confirmation with next steps and event details.' });

      // Reset form
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        nationality: '',
        skills: '',
        availability: [],
        interest: '',
        address: '',
        category: '',
        mediaRole: '',
        gender: '',
        tshirtSize: '',
        website: ''
      });
    } catch (error) {
      // Handle HTTP error responses (like 409 for duplicate email)
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        if (errorData.success === false && errorData.errors) {
          setMessage({
            type: 'error',
            text: errorData.errors.join(', ')
          });
        } else if (errorData.error) {
          setMessage({
            type: 'error',
            text: errorData.error
          });
        } else {
          setMessage({
            type: 'error',
            text: 'Registration failed. Please try again.'
          });
        }
      } else {
        console.log('Backend not available, simulating successful registration for demo purposes');
        setMessage({
          type: 'success',
          text: 'Volunteer registration submitted successfully! Check your email for a confirmation with next steps and event details.'
        });
      }

      // Reset form
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        nationality: '',
        skills: '',
        availability: '',
        interest: '',
        address: '',
        category: '',
        mediaRole: '',
        gender: '',
        tshirtSize: '',
        website: ''
      });
    } finally {
      setLoading(false);
    }
  };

  if (deadlinePassed && volunteerDeadline) {
    return (
      <Container maxWidth="md">
        <AnimatedSection animation="fadeIn">
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'error.main' }}>
              Registration Closed
            </Typography>
            <Typography variant="h6" color="text.secondary" paragraph>
              Volunteer registration has closed. Thank you for your interest in volunteering for the Lagos Impact Hackathon!
            </Typography>
          </Box>
        </AnimatedSection>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <AnimatedSection animation="fadeIn">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Box sx={{
            position: 'relative',
            textAlign: 'center',
            my: 4,
            py: 6,
            background: `
              linear-gradient(135deg, rgba(76, 175, 80, 0.8), rgba(139, 195, 74, 0.8)),
              url('https://images.unsplash.com/photo-1559027615-cd4628902d4a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1974&q=80')
            `,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            color: 'white',
            borderRadius: 4,
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.4)',
              zIndex: 1
            }
          }}>
            <Box sx={{ position: 'relative', zIndex: 2 }}>
              <Typography
                variant="h3"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 'bold',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                  mb: 2
                }}
              >
                Volunteer for Lagos Impact Hackathon
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                paragraph
                sx={{
                  color: 'white',
                  opacity: 0.9,
                  textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                  maxWidth: 600,
                  mx: 'auto'
                }}
              >
                Help make the Lagos Impact Hackathon a success! Join our volunteer team and be part of this transformative event.
              </Typography>
            </Box>
          </Box>
        </motion.div>
      </AnimatedSection>

      {/* Countdown Timer */}
      <AnimatedSection animation="slideUp" delay={0.2}>
        <Typography variant="subtitle1" color="error" sx={{ mb: 1, fontWeight: 600 }}>
          Volunteer Registration Deadline: {volunteerDeadline
            ? volunteerDeadline.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
            : fallbackDeadline.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
        </Typography>
        <CountdownTimer
          targetDate={volunteerDeadline ? volunteerDeadline.toISOString() : fallbackDeadline.toISOString()}
          variant="compact"
          showIcon={true}
        />
      </AnimatedSection>

      {/* Registration Form */}
      <AnimatedSection animation="fadeIn" delay={0.25}>
        <Card sx={{ mt: 4, mb: 8 }}>
          <CardContent sx={{ p: 4 }}>
            {message.text && (
              <Alert severity={message.type} sx={{ mb: 3 }}>
                {message.text}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Full Name *"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email *"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone Number *"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={countries}
                    value={formData.nationality}
                    onChange={(event, newValue) => {
                      setFormData({ ...formData, nationality: newValue || '' });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Nationality *"
                        required
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Gender *</InputLabel>
                    <Select
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                      label="Gender *"
                      required
                    >
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Female">Female</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>T-Shirt Size *</InputLabel>
                    <Select
                      name="tshirtSize"
                      value={formData.tshirtSize}
                      onChange={handleChange}
                      label="T-Shirt Size *"
                      required
                    >
                      <MenuItem value="XS">XS</MenuItem>
                      <MenuItem value="S">S</MenuItem>
                      <MenuItem value="M">M</MenuItem>
                      <MenuItem value="L">L</MenuItem>
                      <MenuItem value="XL">XL</MenuItem>
                      <MenuItem value="XXL">XXL</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Volunteer Category *</InputLabel>
                    <Select
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      label="Volunteer Category *"
                      required
                    >
                      {categories.map((category) => (
                        <MenuItem key={category} value={category}>
                          {category}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {formData.category === 'Media Team' && (
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Media Role *</InputLabel>
                      <Select
                        name="mediaRole"
                        value={formData.mediaRole}
                        onChange={handleChange}
                        label="Media Role *"
                        required
                      >
                        {mediaRoles.map((role) => (
                          <MenuItem key={role} value={role}>
                            {role}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Skills and Experience *"
                    name="skills"
                    multiline
                    rows={3}
                    value={formData.skills}
                    onChange={handleChange}
                    required
                    placeholder="Tell us about your relevant skills and experience..."
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                      Availability <span style={{ color: 'red' }}>*</span>
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 1 }}>
                      {availabilityOptions.map((option) => (
                        <label key={option} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <input
                            type="checkbox"
                            name="availability"
                            value={option}
                            checked={formData.availability.includes(option)}
                            onChange={handleChange}
                            required={formData.availability.length === 0}
                          />
                          <span>{option}</span>
                        </label>
                      ))}
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Why do you want to volunteer? *"
                    name="interest"
                    multiline
                    rows={3}
                    value={formData.interest}
                    onChange={handleChange}
                    required
                    placeholder="Share your motivation for volunteering..."
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address"
                    name="address"
                    multiline
                    rows={2}
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Your address (optional)"
                  />
                </Grid>

                {/* Honeypot field to trap bots */}
                <input
                  type="text"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  style={{ display: 'none' }}
                  autoComplete="off"
                  tabIndex={-1}
                />

                <Grid item xs={12}>
                  <Box sx={{ textAlign: 'center', mt: 2 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={loading}
                      sx={{
                        bgcolor: '#4caf50',
                        '&:hover': { bgcolor: '#388e3c' },
                        px: 4,
                        py: 1.5
                      }}
                    >
                      {loading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        'Submit Volunteer Registration'
                      )}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </AnimatedSection>
    </Container>
  );
}

export default VolunteerRegister;
